import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Shield, Zap, Lock, User, AlertTriangle, KeyRound } from 'lucide-react';
import { useAdminStore } from '../store/adminStore';

const CORRECT_PIN = '2025';

export default function LoginPage() {
  const [step, setStep] = useState<'credentials' | 'pin'>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);

  // verifyCredentials validates without changing auth state.
  // login() sets isAuthenticated=true and is only called after PIN is verified.
  const { login, verifyCredentials, isAuthenticated } = useAdminStore();
  const navigate = useNavigate();

  // If the user is already authenticated (e.g. refreshed /ktec-gate-access while
  // still logged in, or navigated here manually), redirect them immediately.
  useEffect(() => {
    if (isAuthenticated) {
      const redirect = localStorage.getItem('ktec_redirect_attempt') || '/admin/dashboard';
      localStorage.removeItem('ktec_redirect_attempt');
      navigate(redirect, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally only on mount — login sets isAuthenticated and navigates inline

  useEffect(() => {
    if (lockedUntil && Date.now() < lockedUntil) {
      const timer = setTimeout(() => setLockedUntil(null), lockedUntil - Date.now());
      return () => clearTimeout(timer);
    }
  }, [lockedUntil]);

  // ── Step 1: validate credentials (does NOT set isAuthenticated) ──────────
  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    await new Promise((r) => setTimeout(r, 600));

    const valid = verifyCredentials(username, password);
    if (valid) {
      setStep('pin');
      setLoading(false);
    } else {
      setAttemptCount((c) => c + 1);
      if (attemptCount >= 4) {
        setLockedUntil(Date.now() + 30000);
        setError('Too many failed attempts. Account locked for 30 seconds.');
      } else {
        setError('Invalid credentials. Please try again.');
      }
      setLoading(false);
    }
  };

  // ── Step 2: verify PIN → call login() → navigate to stored redirect ──────
  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    await new Promise((r) => setTimeout(r, 400));

    if (pin === CORRECT_PIN) {
      // login() sets isAuthenticated=true and logs the activity.
      login(username, password);

      // Navigate to the page the user was originally trying to reach.
      const redirect = localStorage.getItem('ktec_redirect_attempt') || '/admin/dashboard';
      localStorage.removeItem('ktec_redirect_attempt');
      navigate(redirect, { replace: true });
    } else {
      setError('Invalid PIN. Please try again.');
      setPin('');
    }
    setLoading(false);
  };

  const isLocked = lockedUntil && Date.now() < lockedUntil;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4 font-['Inter',_system-ui,_sans-serif]">
      {/* Background Pattern */}
      <div className="fixed inset-0 opacity-[0.015]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #1D1D1F 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[400px] relative z-10"
      >
        {/* Logo */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#7FA86A] to-[#96BE82] shadow-lg shadow-[#7FA86A]/20 mb-6"
          >
            <Zap className="w-7 h-7 text-white" />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <h1 className="text-[28px] font-semibold text-gray-900 tracking-tight">K-Tec Enterprise</h1>
            <p className="text-[13px] text-gray-400 mt-1 font-medium">
              {step === 'credentials' ? 'Admin Portal Access' : 'Secondary Verification'}
            </p>
          </motion.div>
        </div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200/60 shadow-xl shadow-gray-200/40 p-8"
        >
          <AnimatePresence mode="wait">
            {step === 'credentials' ? (
              <motion.form
                key="credentials"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={handleCredentialsSubmit}
              >
                {/* Username */}
                <div className="mb-5">
                  <label className="block text-[12px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Username</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-300" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter username"
                      className="w-full h-12 pl-11 pr-4 rounded-2xl bg-gray-50/80 border border-gray-200/60 text-[14px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 transition-all"
                      required
                      autoComplete="username"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="mb-5">
                  <label className="block text-[12px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-300" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="w-full h-12 pl-11 pr-12 rounded-2xl bg-gray-50/80 border border-gray-200/60 text-[14px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 transition-all"
                      required
                      autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors">
                      {showPassword ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                    </button>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100 mb-5">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-[13px] text-red-600">{error}</span>
                  </motion.div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !!isLocked}
                  className="w-full h-12 rounded-2xl bg-gray-900 text-white text-[14px] font-semibold hover:bg-gray-800 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Shield className="w-4 h-4" />
                      Continue
                    </>
                  )}
                </button>
              </motion.form>
            ) : (
              <motion.form
                key="pin"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handlePinSubmit}
              >
                <div className="text-center mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-3">
                    <KeyRound className="w-6 h-6 text-blue-500" />
                  </div>
                  <p className="text-[13px] text-gray-500">Enter your 4-digit PIN to complete login</p>
                </div>

                {/* PIN Input */}
                <div className="mb-5">
                  <div className="flex justify-center gap-3">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className={`w-14 h-14 rounded-2xl border-2 flex items-center justify-center text-[24px] font-bold transition-all ${
                        pin.length > i ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 bg-gray-50/80'
                      }`}>
                        {pin.length > i ? '•' : ''}
                      </div>
                    ))}
                  </div>
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => { if (e.target.value.length <= 4) setPin(e.target.value); }}
                    className="w-full h-0 opacity-0 absolute"
                    maxLength={4}
                    autoFocus
                  />
                  {/* Clickable PIN display */}
                  <div className="flex justify-center gap-3 mt-[-56px] relative z-10">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="w-14 h-14 cursor-pointer" onClick={() => {
                        const input = document.querySelector('input[type="password"]') as HTMLInputElement;
                        input?.focus();
                      }} />
                    ))}
                  </div>
                </div>

                {error && (
                  <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-100 mb-5">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-[13px] text-red-600">{error}</span>
                  </motion.div>
                )}

                <button
                  type="submit"
                  disabled={loading || pin.length < 4}
                  className="w-full h-12 rounded-2xl bg-gray-900 text-white text-[14px] font-semibold hover:bg-gray-800 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <KeyRound className="w-4 h-4" />
                      Verify PIN
                    </>
                  )}
                </button>

                <button type="button" onClick={() => { setStep('credentials'); setPin(''); setError(''); }}
                  className="w-full mt-3 text-center text-[13px] text-gray-400 hover:text-gray-600 transition-colors">
                  Back to login
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Security Notice */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-6 text-center">
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-gray-400">
            <Shield className="w-3 h-3" />
            <span>Secured with enterprise-grade encryption</span>
          </div>
        </motion.div>

        {/* Demo Credentials */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          className="mt-4 bg-gray-900/5 backdrop-blur-sm rounded-2xl border border-gray-200/40 p-4">
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Demo Access</div>
          <div className="space-y-1">
            <div className="text-[12px] text-gray-600"><span className="text-gray-400">Username:</span> admin</div>
            <div className="text-[12px] text-gray-600"><span className="text-gray-400">Password:</span> admin123</div>
            <div className="text-[12px] text-gray-600"><span className="text-gray-400">PIN:</span> 2025</div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
